import { createSelector } from 'reselect';
import { once, isEqual } from 'underscore';
import { getSettingsValuesWithDefaults as getSettingsValuesWithDefaultsUtil } from '../utils/themes';
import { getThemeFields, getThemeMessages } from './themeSelectors';
import { getIsUngatedForUniversalThemeEditing } from './authSelectors';
import { getBrandSettings } from 'ContentData/selectors/branding';
import { Iterable } from 'immutable';
import { stateSelectorSwitcher } from '../utils/selectorUtils';
const getThemeSettings = state => stateSelectorSwitcher(state, 'themeSettings');
const getModules = state => {
  //support immutable version of modules
  const modules = state.modules;
  return Iterable.isIterable(modules) ? modules === null || modules === void 0 ? void 0 : modules.toJS() : modules;
};

// plugin used in Editor 2.0, uses same theme settings object as theme editor app
const getIsThemeStylesPlugin = state => Boolean(state['plugin-theme-styles']);
const defaultUniversalThemeSettings = {
  values: {},
  breakpointStyles: {},
  activeThemePresetId: undefined
};

// universal theme settings selectors used in Editor 1.0
export const getHasUniversalThemeSettings = createSelector([getIsUngatedForUniversalThemeEditing, getModules, getIsThemeStylesPlugin], (isUngatedForUniversalThemeEditing, modules, isThemeStylesPlugin) => Boolean(modules) && isUngatedForUniversalThemeEditing && !isThemeStylesPlugin);
export const getPresentUniversalThemeSettings = createSelector([getModules], modules => {
  var _modules$present;
  return (modules === null || modules === void 0 || (_modules$present = modules.present) === null || _modules$present === void 0 ? void 0 : _modules$present.themeSettings) || defaultUniversalThemeSettings;
});
export const getFutureUniversalThemeSettings = createSelector([getModules], modules => {
  var _modules$future;
  return (modules === null || modules === void 0 || (_modules$future = modules.future) === null || _modules$future === void 0 ? void 0 : _modules$future.themeSettings) || [];
});
export const getPastUniversalThemeSettings = createSelector([getModules], modules => {
  var _modules$past;
  return (modules === null || modules === void 0 || (_modules$past = modules.past) === null || _modules$past === void 0 ? void 0 : _modules$past.themeSettings) || [];
});
export const getThemeSettingsRequestStatus = createSelector([getThemeSettings], themeSettings => themeSettings.requestStatus);
export const getUniversalThemeSettingsRequestStatus = createSelector([getThemeSettings], themeSettings => themeSettings.contentRequestStatus);
export const getThemeSettingsMeta = createSelector([getThemeSettings], themeSettings => themeSettings.meta);
export const getThemeSettingsLastSave = createSelector([getThemeSettingsMeta], themeSettingsMeta => themeSettingsMeta.updated);
export const getIsThemeSettingsLastSaveUniversal = createSelector([getThemeSettingsMeta], themeSettingsMeta => themeSettingsMeta.fromUniversal);
export const getThemeSettingsLastSaveIsAutoSave = createSelector([getThemeSettingsMeta], themeSettingsMeta => themeSettingsMeta.isAutoSave);
export const getThemeSettingsId = createSelector([getThemeSettingsMeta], themeSettingsMeta => themeSettingsMeta.id);
export const getActiveThemePresetId = createSelector([getThemeSettings, getHasUniversalThemeSettings, getPresentUniversalThemeSettings], (themeSettings, hasUniversalThemeSettings, universalThemeSettings) => {
  var _themeSettings$values;
  const _themeSettings = hasUniversalThemeSettings ? universalThemeSettings : themeSettings === null || themeSettings === void 0 || (_themeSettings$values = themeSettings.values) === null || _themeSettings$values === void 0 ? void 0 : _themeSettings$values.present;
  return (_themeSettings === null || _themeSettings === void 0 ? void 0 : _themeSettings.activeThemePresetId) || undefined;
});
export const getThemeSettingsValues = createSelector([getThemeSettings, getHasUniversalThemeSettings, getPresentUniversalThemeSettings], (themeSettings, hasUniversalThemeSettings, universalThemeSettings) => {
  const themeSettingsValues = hasUniversalThemeSettings ? universalThemeSettings.values : themeSettings.values.present.values;
  return themeSettingsValues || {};
});
const getPastThemeSettingsValues = createSelector([getThemeSettings, getHasUniversalThemeSettings, getPastUniversalThemeSettings], (themeSettings, hasUniversalThemeSettings, universalThemeSettings) => {
  const themeSettingsValues = hasUniversalThemeSettings ? universalThemeSettings.values : themeSettings.values.past;
  return themeSettingsValues || {};
});
const getFutureThemeSettingsValues = createSelector([getThemeSettings, getHasUniversalThemeSettings, getFutureUniversalThemeSettings], (themeSettings, hasUniversalThemeSettings, universalThemeSettings) => {
  const themeSettingsValues = hasUniversalThemeSettings ? universalThemeSettings.values : themeSettings.values.future;
  return themeSettingsValues || {};
});
export const getThemeBreakpointStyles = createSelector([getThemeSettings, getHasUniversalThemeSettings, getPresentUniversalThemeSettings], (themeSettings, hasUniversalThemeSettings, universalThemeSettings) => {
  const themeBreakpointStyles = hasUniversalThemeSettings ? universalThemeSettings.breakpointStyles : themeSettings.values.present.breakpointStyles;
  return themeBreakpointStyles || {};
});
export const getCanUndo = createSelector([getPastThemeSettingsValues], past => !!past.length);
export const getCanRedo = createSelector([getFutureThemeSettingsValues], future => !!future.length);
const _getBrandSettings = state => {
  return getBrandSettings(state);
};
export const getThemeInheritanceDataOverrides = createSelector([getThemeFields, getThemeSettingsValues, _getBrandSettings], (themeFields, themeSettings, brandSettings) => {
  return {
    themeSettings,
    themeFields,
    brandSettings
  };
});
const makeGetSettingsFormSpec = once(() => {
  const initialSpec = {
    fields: [],
    messages: {}
  };
  let cachedSpec = initialSpec;
  return createSelector([getThemeFields, getThemeMessages], (fields, messages) => {
    const newSpec = Object.assign({}, initialSpec, {
      fields,
      messages
    });
    if (!isEqual(newSpec, cachedSpec)) {
      cachedSpec = newSpec;
      return newSpec;
    }
    return cachedSpec;
  });
});
export const getSettingsFormSpec = makeGetSettingsFormSpec();
const makeGetSettingsValuesWithDefaults = once(() => {
  const initialValues = {};
  let cachedValues = initialValues;
  return createSelector([getThemeFields, getThemeSettings], (fields, settingsValues) => {
    const newValues = getSettingsValuesWithDefaultsUtil(fields, settingsValues);
    if (!isEqual(newValues, cachedValues)) {
      cachedValues = newValues;
    }
    return cachedValues;
  });
});
export const getSettingsValuesWithDefaults = makeGetSettingsValuesWithDefaults();